import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { NumberCompare } from "@/components/NumberCompare";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { Smartphone, Hexagon, Loader2, Sparkles, TriangleAlert, Compass, Save, Star, Scale, Plus, BookOpen, TableProperties, ChevronDown, HeartPulse, Printer, ShieldAlert, Wand2, Gift, Download, Share2 } from "lucide-react";

const L = (lang, zh, en) => (lang === "zh" ? zh : en);

const INTENTS = [
  { key: "财运", en: "Wealth" },
  { key: "事业", en: "Career" },
  { key: "人缘", en: "Network" },
  { key: "感情", en: "Love" },
  { key: "健康", en: "Health" },
  { key: "平稳", en: "Stability" },
];

async function saveRecord(kind, result, ai_reading, label, lang, okMsg) {
  try {
    await api.post("/numerology/save", { kind, result, ai_reading, label });
    toast.success(okMsg || L(lang, "已保存到我的记录（个人页面查看）", "Saved to your records (see Profile)"));
  } catch (e) {
    toast.error(e.response?.data?.detail || "Error");
  }
}

const SaveButton = ({ onClick, lang }) => (
  <Button size="sm" variant="outline" onClick={onClick} data-testid="numerology-save" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]">
    <Save className="h-4 w-4 mr-1" />{L(lang, "保存记录", "Save")}
  </Button>
);

const CompareButton = ({ onClick, lang }) => (
  <Button size="sm" variant="outline" onClick={onClick} data-testid="numerology-add-compare" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]">
    <Scale className="h-4 w-4 mr-1" />{L(lang, "加入对比", "Add to compare")}
  </Button>
);

/* --------------- shared visualisations --------------- */
const STAR_ORDER = ["天医", "生气", "延年", "伏位", "绝命", "五鬼", "六煞", "祸害"];

function StarCharts({ starScores, lang }) {
  const radarData = STAR_ORDER.map((s) => {
    const hit = (starScores || []).find((x) => x.star === s);
    return { star: s, count: hit ? hit.count : 0 };
  });
  const barData = (starScores || []).filter((x) => x.count > 0).map((x) => ({ ...x }));
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="phone-star-charts">
      <Card className="border-border"><CardContent className="p-3">
        <div className="text-xs font-semibold text-[hsl(var(--brand-bronze))] mb-1">{L(lang, "八星分布（雷达）", "Eight-Star radar")}</div>
        <ResponsiveContainer width="100%" height={220}>
          <RadarChart data={radarData} outerRadius="72%">
            <PolarGrid stroke="hsl(var(--border))" />
            <PolarAngleAxis dataKey="star" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
            <Radar dataKey="count" stroke="hsl(var(--brand-bronze))" fill="hsl(var(--brand-gold))" fillOpacity={0.45} />
            <Tooltip />
          </RadarChart>
        </ResponsiveContainer>
      </CardContent></Card>
      <Card className="border-border"><CardContent className="p-3">
        <div className="text-xs font-semibold text-[hsl(var(--brand-bronze))] mb-1">{L(lang, "各磁场累计能量分", "Field energy score")}</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={barData} margin={{ top: 6, right: 8, bottom: 0, left: -22 }}>
            <XAxis dataKey="star" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
            <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "hsl(var(--brand-gold)/0.08)" }} />
            <Bar dataKey="score" radius={[4, 4, 0, 0]}>
              {barData.map((e, i) => <Cell key={i} fill={e.type === "auspicious" ? "hsl(var(--brand-gold))" : "hsl(var(--brand-vermillion))"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent></Card>
    </div>
  );
}

function SegmentTimeline({ segments, lang }) {
  if (!segments || segments.length === 0) return null;
  return (
    <div data-testid="phone-segment-timeline">
      <div className="text-xs font-semibold text-[hsl(var(--brand-bronze))] mb-2">{L(lang, "号码磁场分段（吉凶时间轴）", "Field segments (auspicious / inauspicious)")}</div>
      <div className="flex items-stretch gap-1 overflow-x-auto pb-2">
        {segments.map((s, i) => (
          <div key={i} className="shrink-0 text-center" style={{ minWidth: 52 }}>
            <div className={`rounded-md py-1.5 text-xs font-bold ${s.type === "auspicious" ? "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]" : "bg-[hsl(var(--brand-vermillion))] text-white"}`}>
              {s.pair}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1 leading-tight">{s.star}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1">
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[hsl(var(--brand-gold))]" />{L(lang, "吉", "Auspicious")}</span>
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-[hsl(var(--brand-vermillion))]" />{L(lang, "凶", "Inauspicious")}</span>
      </div>
    </div>
  );
}

function Reading({ reading, lang }) {
  if (!reading) return null;
  return (
    <div className="mt-4 space-y-3" data-testid="numerology-ai-reading">
      {reading.summary && (
        <div className="rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.2)] p-4">
          <div className="flex items-center gap-2 mb-1 text-[hsl(var(--brand-bronze))]"><Sparkles className="h-4 w-4" /><span className="font-semibold text-sm">{L(lang, "AI 解读", "AI Reading")}</span></div>
          <p className="text-sm leading-relaxed text-foreground">{reading.summary}</p>
        </div>
      )}
      {reading.report && (
        <div className="rounded-lg border border-border bg-card p-4" data-testid="numerology-star-report">
          <div className="flex items-center gap-2 mb-1.5 text-[hsl(var(--brand-bronze))]"><BookOpen className="h-4 w-4" /><span className="font-semibold text-sm">{L(lang, "星耀释义 · 详细报告", "Star Report")}</span></div>
          <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{reading.report}</p>
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-3">
        {[["lucky", L(lang, "宜 / 有利", "Favourable"), "text-[hsl(var(--brand-bronze))]"], ["cautions", L(lang, "注意 / 化解", "Cautions"), "text-[hsl(var(--brand-vermillion))]"], ["advice", L(lang, "行动建议", "Advice"), "text-foreground"]].map(([k, title, cls]) => (
          (reading[k] || []).length > 0 && (
            <Card key={k} className="border-border"><CardContent className="p-3">
              <div className={`text-xs font-bold mb-1.5 ${cls}`}>{title}</div>
              <ul className="space-y-1.5">
                {reading[k].map((it, i) => <li key={i} className="text-xs text-muted-foreground leading-relaxed">· {it}</li>)}
              </ul>
            </CardContent></Card>
          )
        ))}
      </div>
    </div>
  );
}

function StarTable({ lang }) {
  const [rows, setRows] = useState(null);
  const [note, setNote] = useState({ zh: "", en: "" });
  const [probe, setProbe] = useState("");
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ciao_probe_recent") || "[]"); } catch { return []; }
  });
  React.useEffect(() => {
    api.get("/numerology/star-table")
      .then((r) => { setRows(r.data.stars); setNote({ zh: r.data.amplifier_note_zh, en: r.data.amplifier_note_en }); })
      .catch(() => setRows([]));
  }, []);

  // client-side pair -> star lookup (mirror of backend rule)
  const pairMap = React.useMemo(() => {
    const m = {};
    (rows || []).forEach((r) => r.pairs.forEach((p) => { m[p] = r.star; }));
    return m;
  }, [rows]);
  const pd = probe.replace(/\D/g, "").slice(0, 2);
  const starOf = React.useCallback((p) => (p.includes("0") || p.includes("5")) ? "伏位" : (pairMap[p] || "伏位"), [pairMap]);
  const probeStar = pd.length === 2 ? starOf(pd) : null;
  const probeRow = probeStar ? (rows || []).find((r) => r.star === probeStar) : null;
  const rowByStar = React.useCallback((star) => (rows || []).find((r) => r.star === star), [rows]);

  // 最近查询记忆
  React.useEffect(() => {
    if (pd.length !== 2 || !rows) return;
    const t = setTimeout(() => {
      setRecent((prev) => {
        const next = [pd, ...prev.filter((x) => x !== pd)].slice(0, 8);
        try { localStorage.setItem("ciao_probe_recent", JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    }, 700);
    return () => clearTimeout(t);
  }, [pd, rows]);
  const clearRecent = () => { setRecent([]); try { localStorage.removeItem("ciao_probe_recent"); } catch { /* ignore */ } };

  return (
    <Accordion type="single" collapsible className="rounded-xl border border-border" data-testid="star-table">
      <AccordionItem value="table" className="border-none">
        <AccordionTrigger className="px-4 hover:no-underline">
          <span className="flex items-center gap-2 text-sm font-semibold text-[hsl(var(--brand-bronze))]"><TableProperties className="h-4 w-4" />{L(lang, "八星磁场配对速查表", "Eight-Star pairing reference")}</span>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          {!rows ? (
            <div className="py-4 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : (
            <div className="space-y-2">
              {/* 小测: 输入两位数字即时判定 */}
              <div className="rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.15)] p-3" data-testid="star-probe">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-[hsl(var(--brand-bronze))]">{L(lang, "磁场小测", "Quick check")}</span>
                  <Input value={probe} onChange={(e) => setProbe(e.target.value)} inputMode="numeric" maxLength={2}
                    placeholder={L(lang, "输入两位数字，如 27", "2 digits, e.g. 27")} className="h-8 w-40 font-mono-num" data-testid="star-probe-input" />
                  {probeStar && probeRow && (
                    <span className="inline-flex items-center gap-1.5 text-sm" data-testid="star-probe-result">
                      <span className="font-mono-num font-bold">{pd}</span>
                      <span className="text-muted-foreground">→</span>
                      <Badge className={probeRow.type === "auspicious" ? "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]" : "bg-[hsl(var(--brand-vermillion))] text-white"}>{probeStar}</Badge>
                      <span className={`text-xs font-semibold ${probeRow.type === "auspicious" ? "text-[hsl(var(--brand-bronze))]" : "text-[hsl(var(--brand-vermillion))]"}`}>{probeRow.type === "auspicious" ? L(lang, "吉", "Lucky") : L(lang, "凶", "Unlucky")}</span>
                    </span>
                  )}
                </div>
                {recent.length > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="star-probe-recent">
                    <span className="text-[11px] text-muted-foreground">{L(lang, "最近查询", "Recent")}：</span>
                    {recent.map((p) => {
                      const r = rowByStar(starOf(p));
                      const good = r?.type === "auspicious";
                      return (
                        <button key={p} type="button" onClick={() => setProbe(p)} data-testid={`star-probe-recent-${p}`}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-mono-num transition-colors ${good ? "border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))] hover:bg-[hsl(var(--brand-gold-soft)/0.35)]" : "border-[hsl(var(--brand-vermillion)/0.5)] text-[hsl(var(--brand-vermillion))] hover:bg-[hsl(var(--brand-vermillion)/0.08)]"}`}>
                          {p}<span className="opacity-70">{starOf(p)}</span>
                        </button>
                      );
                    })}
                    <button type="button" onClick={clearRecent} className="text-[11px] text-muted-foreground underline hover:text-foreground ml-1" data-testid="star-probe-recent-clear">{L(lang, "清除", "Clear")}</button>
                  </div>
                )}
              </div>

              {rows.map((r) => {
                const hit = probeStar === r.star;
                return (
                  <div key={r.star} className={`rounded-lg border p-2.5 transition-all ${hit ? "ring-2 ring-[hsl(var(--brand-gold))] shadow-sm" : ""} ${r.type === "auspicious" ? "border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.12)]" : "border-[hsl(var(--brand-vermillion)/0.4)] bg-[hsl(var(--brand-vermillion)/0.05)]"}`} data-testid={`star-row-${r.star}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="flex items-center gap-2">
                        <Badge className={r.type === "auspicious" ? "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]" : "bg-[hsl(var(--brand-vermillion))] text-white"}>{r.star}</Badge>
                        <span className="text-xs text-muted-foreground">{L(lang, r.zh, r.desc_en)}</span>
                      </span>
                      <span className={`text-xs font-semibold ${r.type === "auspicious" ? "text-[hsl(var(--brand-bronze))]" : "text-[hsl(var(--brand-vermillion))]"}`}>{r.type === "auspicious" ? L(lang, "吉", "Lucky") : L(lang, "凶", "Unlucky")}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {r.pairs.map((p) => <span key={p} className={`font-mono-num text-xs rounded border px-1.5 py-0.5 ${hit && p === pd ? "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-gold))] font-bold" : "bg-card border-border text-foreground"}`}>{p}</span>)}
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-muted-foreground pt-1">{L(lang, note.zh, note.en)}</p>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

const NUM_TYPES = [
  { key: "phone", zh: "手机", en: "Phone", min: 9, max: 12, placeholder_zh: "如 0123456789", placeholder_en: "e.g. 0123456789", hint_zh: "手机号一般为 10-11 位数字", hint_en: "Phone numbers are usually 10-11 digits" },
  { key: "ic", zh: "身份证", en: "IC", exact: 12, placeholder_zh: "如 880101145678（12位）", placeholder_en: "e.g. 880101145678 (12 digits)", hint_zh: "大马身份证为 12 位数字", hint_en: "Malaysian IC is 12 digits" },
  { key: "plate", zh: "车牌", en: "Plate", min: 3, placeholder_zh: "如 ABC1234（字母按所选模式换算）", placeholder_en: "e.g. ABC1234 (letters converted)", hint_zh: "车牌可含字母，将按所选模式换算", hint_en: "Plates may include letters (converted)" },
  { key: "name", zh: "英文名", en: "Name", min: 2, placeholder_zh: "如 NICE / ALAN（输入英文名查吉凶）", placeholder_en: "e.g. NICE / ALAN", hint_zh: "输入英文名，查看其数字能量吉凶", hint_en: "Enter an English name to see its number energy" },
  { key: "other", zh: "其他", en: "Other", min: 3, placeholder_zh: "任意数字或字母，至少 3 位", placeholder_en: "Any digits/letters, min 3", hint_zh: "任意数字或字母组合", hint_en: "Any number or letter combination" },
];

const CONV_MODES = [
  { key: "position", zh: "字母序号", en: "Position", tip_zh: "A=1、B=2 … N=14 … Z=26", tip_en: "A=1, B=2 … N=14 … Z=26" },
  { key: "root", zh: "数根", en: "Digit Root", tip_zh: "A=1 … N=5（逐位相加至个位）", tip_en: "reduced to a single digit (N=5)" },
];

function ModeToggle({ mode, setMode, lang }) {
  return (
    <div data-testid="conv-mode-toggle">
      <Label className="text-xs text-muted-foreground">{L(lang, "字母换算模式", "Letter conversion mode")}</Label>
      <div className="flex gap-2 mt-1">
        {CONV_MODES.map((m) => (
          <Button key={m.key} type="button" size="sm" variant={mode === m.key ? "default" : "outline"}
            title={L(lang, m.tip_zh, m.tip_en)}
            className={mode === m.key ? "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-gold))]" : ""}
            onClick={() => setMode(m.key)} data-testid={`conv-mode-${m.key}`}>
            {L(lang, m.zh, m.en)}
          </Button>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">{L(lang, (CONV_MODES.find((x) => x.key === mode) || {}).tip_zh, (CONV_MODES.find((x) => x.key === mode) || {}).tip_en)}</p>
    </div>
  );
}

// 换算过程展示: 逐字符显示 N→14 等; 字母来源的数字高亮 (车牌/英文名直观)
function ConversionView({ conversion, lang }) {
  const items = conversion || [];
  if (!items.length || !items.some((i) => i.type === "letter")) return null;
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3" data-testid="conversion-view">
      <div className="text-xs font-semibold text-[hsl(var(--brand-bronze))] mb-1.5">{L(lang, "换算过程", "Conversion")}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {items.map((it, i) => (
          <span key={i} className={`inline-flex items-center gap-0.5 rounded px-1.5 py-1 text-xs ${it.type === "letter" ? "bg-[hsl(var(--brand-gold)/0.25)] text-[hsl(var(--brand-bronze))] font-semibold" : "bg-card border border-border text-muted-foreground"}`}
            data-testid={`conv-item-${i}`}>
            <span className="font-mono-num">{it.char}</span>
            {it.type === "letter" && <><span className="opacity-60">→</span><span className="font-mono-num">{it.digits}</span></>}
          </span>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5">{L(lang, "金色为英文字母换算出的数字", "Gold = digits converted from letters")}</p>
    </div>
  );
}

function PhoneTool({ lang }) {
  const [phone, setPhone] = useState("");
  const [numType, setNumType] = useState("phone");
  const [mode, setMode] = useState("position");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [fix, setFix] = useState(null);
  const [fixBusy, setFixBusy] = useState(false);
  const [nameSug, setNameSug] = useState(null);
  const [nameSugBusy, setNameSugBusy] = useState(false);
  const cfg = NUM_TYPES.find((t) => t.key === numType) || NUM_TYPES[3];

  const run = async () => {
    const digits = (phone.match(/[0-9a-zA-Z]/g) || []).length;   // 字母也计入(会换算成数字)
    if (digits < 3) { toast.error(L(lang, "请输入至少 3 位数字或字母", "Enter at least 3 digits/letters")); return; }
    if (cfg.exact && digits !== cfg.exact) {
      toast.error(L(lang, `${cfg.zh}应为 ${cfg.exact} 位，当前 ${digits} 位`, `${cfg.en} should be ${cfg.exact} digits, got ${digits}`)); return;
    }
    if (cfg.min && digits < cfg.min) {
      toast.error(L(lang, `${cfg.zh}至少 ${cfg.min} 位，当前 ${digits} 位`, `${cfg.en} needs at least ${cfg.min}, got ${digits}`)); return;
    }
    if (cfg.max && digits > cfg.max) {
      toast.error(L(lang, `${cfg.zh}最多 ${cfg.max} 位，当前 ${digits} 位`, `${cfg.en} allows at most ${cfg.max}, got ${digits}`)); return;
    }
    setBusy(true); setRes(null); setFix(null); setNameSug(null);
    try {
      const r = await api.post("/numerology/phone", { phone, lang, mode });
      setRes(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const runNameSuggest = async () => {
    setNameSugBusy(true); setNameSug(null);
    try {
      const r = await api.post("/numerology/name-suggest", { phone, lang, mode });
      setNameSug(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setNameSugBusy(false); }
  };

  const runFix = async () => {
    setFixBusy(true); setFix(null);
    try {
      const r = await api.post("/numerology/suggest-fix", { phone, lang, mode });
      setFix(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setFixBusy(false); }
  };

  const result = res?.result;
  const de = result?.dominant_explain;
  return (
    <div className="space-y-4">
      <StarTable lang={lang} />
      <div>
        <Label>{L(lang, "号码类型", "Number type")}</Label>
        <div className="flex flex-wrap gap-2 mt-1" data-testid="numtype-tags">
          {NUM_TYPES.map((t) => (
            <Button key={t.key} type="button" size="sm" variant={numType === t.key ? "default" : "outline"}
              className={numType === t.key ? "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-gold))]" : "border-border"}
              onClick={() => setNumType(t.key)} data-testid={`numtype-${t.key}`}>{L(lang, t.zh, t.en)}</Button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-1" data-testid="numtype-hint">{L(lang, cfg.hint_zh, cfg.hint_en)}</p>
      </div>
      <ModeToggle mode={mode} setMode={setMode} lang={lang} />
      <div className="flex flex-col sm:flex-row gap-2 items-end">
        <div className="flex-1 w-full">
          <Label>{L(lang, "号码 / 数字（可含英文字母）", "Number (letters allowed)")}</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={L(lang, cfg.placeholder_zh, cfg.placeholder_en)} data-testid="phone-input" />
        </div>
        <Button onClick={run} disabled={busy} className="bg-[hsl(var(--primary))] text-primary-foreground w-full sm:w-auto" data-testid="phone-analyze">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L(lang, "分析", "Analyze")}
        </Button>
      </div>

      {busy && <div className="py-8 text-center text-muted-foreground flex flex-col items-center gap-2"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-bronze))]" />{L(lang, "计算与解读中…", "Calculating…")}</div>}

      {result && (
        <div data-testid="phone-result" className="space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm font-semibold text-muted-foreground">{L(lang, "分析结果", "Result")}</span>
            <div className="flex gap-2">
              <CompareButton lang={lang} onClick={() => saveRecord("phone", result, res.ai_reading, result.phone, lang, L(lang, "已加入对比（在「对比」标签查看）", "Added to compare (see Compare tab)"))} />
              <SaveButton lang={lang} onClick={() => saveRecord("phone", result, res.ai_reading, result.phone, lang)} />
            </div>
          </div>

          {result.had_letters && (
            <div className="rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.15)] p-2.5 text-xs text-[hsl(var(--brand-bronze))]" data-testid="phone-normalized-note">
              {L(lang, "英文字母已换算为数字：", "Letters converted to digits: ")}
              <span className="font-mono-num font-semibold">{result.input_raw}</span>
              <span className="mx-1">→</span>
              <span className="font-mono-num font-semibold">{result.phone}</span>
            </div>
          )}

          <ConversionView conversion={result.conversion} lang={lang} />

          {result.suitability && (
            <div className="rounded-xl border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.16)] p-3" data-testid="suitability-card">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--brand-bronze))]">
                  <Compass className="h-4 w-4" />{L(lang, numType === "plate" ? "车牌吉凶速查 · 是否契合你" : "是否契合你的命盘", numType === "plate" ? "Plate fit for you" : "Fit for your chart")}
                </div>
                <Badge className="bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]" data-testid="suitability-verdict">{L(lang, result.suitability.verdict_zh, result.suitability.verdict_en)}</Badge>
              </div>
              <ul className="mt-2 space-y-1">
                {(lang === "zh" ? result.suitability.reasons_zh : result.suitability.reasons_en).map((r, i) => (
                  <li key={i} className="text-[11px] text-muted-foreground flex gap-1.5"><span className="text-[hsl(var(--brand-gold))]">·</span>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {numType === "name" && (
            <div className="rounded-xl border border-[hsl(var(--brand-gold)/0.5)] bg-[hsl(var(--brand-gold-soft)/0.12)] p-3" data-testid="name-suggest-box">
              <Button size="sm" onClick={runNameSuggest} disabled={nameSugBusy} className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="name-suggest-run">
                {nameSugBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}{L(lang, "换个拼写更吉", "Suggest a luckier spelling")}
              </Button>
              {nameSug && (
                <div className="mt-2" data-testid="name-suggest-result">
                  <p className="text-[11px] text-muted-foreground">{L(lang, nameSug.message_zh, nameSug.message_en)} {L(lang, `原名 ${nameSug.name} · ${nameSug.base_score}分`, `${nameSug.name} · ${nameSug.base_score}`)}</p>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2">
                    {(nameSug.suggestions || []).map((s, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg border border-[hsl(var(--brand-gold))] bg-card px-2.5 py-1.5" data-testid={`name-suggest-${i}`}>
                        <div>
                          <span className="font-semibold text-[hsl(var(--brand-bronze))]">{s.name}</span>
                          <span className="ml-1.5 text-[10px] text-muted-foreground">{L(lang, { change: "改字母", add: "加字母", remove: "减字母" }[s.change_type], s.change_type)}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono-num text-sm font-semibold text-[hsl(var(--brand-ink))]">{s.score}</span>
                          <span className="ml-1 text-[10px] text-[hsl(var(--brand-gold))]">▲{s.delta_score}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(nameSug.suggestions || []).length === 0 && <p className="text-[11px] text-muted-foreground mt-1">{L(lang, "暂无更优建议。", "No better suggestion.")}</p>}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-4 rounded-xl border border-border p-4">
            <div className="text-center">
              <div className="text-xs text-muted-foreground">{L(lang, "综合评分", "Score")}</div>
              <div className="font-mono-num text-3xl font-bold text-[hsl(var(--brand-vermillion))]">{result.score}</div>
            </div>
            <div className="h-10 w-px bg-border" />
            <div>
              <div className="text-xs text-muted-foreground">{L(lang, "主导磁场", "Dominant field")}</div>
              <div className="font-display text-lg font-semibold text-[hsl(var(--brand-ink))]">{result.dominant}</div>
            </div>
          </div>

          {de && (
            <div className="rounded-xl border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.15)] p-4" data-testid="phone-dominant-explain">
              <div className="text-xs font-semibold text-[hsl(var(--brand-bronze))] mb-1">{L(lang, "主导磁场解析", "Dominant field analysis")} · {de.star}</div>
              <p className="text-sm text-foreground leading-relaxed">{L(lang, de.zh, de.en)}</p>
              {(de.suitable_intents || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1 items-center">
                  <span className="text-xs text-muted-foreground">{L(lang, "较适合：", "Suited for: ")}</span>
                  {de.suitable_intents.map((it) => <Badge key={it} variant="outline" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))] text-[10px]">{L(lang, it, (INTENTS.find((x) => x.key === it) || {}).en || it)}</Badge>)}
                </div>
              )}
            </div>
          )}

          <SegmentTimeline segments={result.segments} lang={lang} />
          <StarCharts starScores={result.star_scores} lang={lang} />

          <div className="grid sm:grid-cols-2 gap-2">
            {result.stars.map((s) => (
              <div key={s.star} className="flex items-start gap-2 rounded-lg border border-border p-2.5">
                <Badge className={s.type === "auspicious" ? "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]" : "bg-[hsl(var(--brand-vermillion))] text-white"}>{s.star} ×{s.count}</Badge>
                <span className="text-xs text-muted-foreground">{L(lang, s.zh, s.desc_en)}</span>
              </div>
            ))}
          </div>

          {(result.warnings || []).length > 0 && (
            <div className="rounded-xl border border-[hsl(var(--brand-vermillion)/0.5)] bg-[hsl(var(--brand-vermillion)/0.06)] p-3" data-testid="phone-warnings">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--brand-vermillion))]">
                <ShieldAlert className="h-4 w-4" />{L(lang, "避忌提醒 · 化解建议", "Caution & Remedies")}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">{L(lang, result.taboo_summary_zh, result.taboo_summary_en)}</p>
              <div className="space-y-2">
                {result.warnings.map((w, i) => (
                  <div key={i} className="rounded-lg border border-[hsl(var(--brand-vermillion)/0.3)] bg-card p-2.5" data-testid={`phone-warning-${i}`}>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-[hsl(var(--brand-vermillion))] text-white text-[10px]"><span className="font-mono-num mr-1">{w.pair}</span>{w.star}</Badge>
                      <span className="text-[11px] text-muted-foreground">{L(lang, w.label_zh, w.label_en)}</span>
                    </div>
                    {w.remedy_zh && (
                      <p className="mt-1 text-[11px] leading-5 text-foreground/80">
                        <span className="font-medium text-[hsl(var(--brand-bronze))]">{L(lang, "化解：", "Remedy: ")}</span>
                        {L(lang, w.remedy_zh, w.remedy_en)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">{L(lang, "仅供参考，不构成任何决策建议", "For reference only; not decision advice")}</p>

              <div className="mt-2">
                <Button size="sm" onClick={runFix} disabled={fixBusy} className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="phone-suggest-fix">
                  {fixBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}{L(lang, "一键换号建议", "Suggest a fix")}
                </Button>
              </div>

              {fix && fix.suggestion && (
                <div className="mt-2 rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.18)] p-3" data-testid="phone-fix-result">
                  <p className="text-[11px] text-muted-foreground">{L(lang, fix.message_zh, fix.message_en)}</p>
                  <div className="mt-2 flex items-center justify-center gap-3">
                    <div className="text-center">
                      <div className="text-[10px] text-muted-foreground">{L(lang, "原号码", "Original")}</div>
                      <div className="font-mono-num text-lg font-semibold text-[hsl(var(--brand-vermillion))] line-through">{fix.original}</div>
                      <div className="text-[10px] text-muted-foreground">{fix.original_score}{L(lang, "分", "")} · {fix.original_warnings}{L(lang, "处忌讳", " warn")}</div>
                    </div>
                    <ChevronDown className="h-5 w-5 -rotate-90 text-[hsl(var(--brand-bronze))]" />
                    <div className="text-center">
                      <div className="text-[10px] text-muted-foreground">{L(lang, "推荐替换为", "Suggested")}</div>
                      <div className="font-mono-num text-lg font-bold text-[hsl(var(--brand-bronze))]" data-testid="phone-fix-suggestion">
                        {fix.suggestion.split("").map((d, i) => {
                          const ch = (fix.changes || []).some((c) => c.index === i);
                          return <span key={i} className={ch ? "text-[hsl(var(--brand-gold))] underline decoration-2 underline-offset-2" : ""}>{d}</span>;
                        })}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{fix.suggestion_score}{L(lang, "分", "")} · {fix.suggestion_warnings}{L(lang, "处忌讳", " warn")}</div>
                    </div>
                  </div>
                  <p className="mt-1.5 text-[10px] text-center text-muted-foreground">{L(lang, "金色下划线为改动的数字", "Gold underline marks changed digits")}</p>
                </div>
              )}
              {fix && !fix.suggestion && (
                <p className="mt-2 text-[11px] text-muted-foreground" data-testid="phone-fix-result">{L(lang, fix.message_zh, fix.message_en)}</p>
              )}
            </div>
          )}

          <Reading reading={res.ai_reading} lang={lang} />
          {!res.ai_reading && <p className="text-xs text-muted-foreground">{L(lang, "（AI 解读暂时不可用，以上为规则计算结果）", "(AI reading unavailable; rule-based result shown)")}</p>}
        </div>
      )}
    </div>
  );
}

function HexagramTool({ lang }) {
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [n3, setN3] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    const numbers = [n1, n2, n3].filter((x) => x !== "").map((x) => parseInt(x, 10)).filter((x) => !isNaN(x));
    if (numbers.length === 0) { toast.error(L(lang, "请至少输入一个数字", "Enter at least one number")); return; }
    setBusy(true); setRes(null);
    try {
      const r = await api.post("/numerology/hexagram", { numbers, question: q, lang });
      setRes(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const r = res?.result;
  const GuaCard = ({ title, gua }) => (
    <div className="flex-1 rounded-xl border border-border p-4 text-center">
      <div className="text-xs text-muted-foreground mb-1">{title}</div>
      <div className="text-3xl leading-none">{gua.upper.symbol}<br />{gua.lower.symbol}</div>
      <div className="mt-2 font-display text-lg font-bold text-[hsl(var(--brand-ink))]">{gua.name}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{L(lang, `上${gua.upper.zh} · 下${gua.lower.zh}`, `${gua.upper.zh}/${gua.lower.zh}`)}</div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        <div><Label>{L(lang, "数字一", "Number 1")}</Label><Input value={n1} onChange={(e) => setN1(e.target.value)} inputMode="numeric" placeholder="e.g. 3" data-testid="hex-n1" /></div>
        <div><Label>{L(lang, "数字二", "Number 2")}</Label><Input value={n2} onChange={(e) => setN2(e.target.value)} inputMode="numeric" placeholder="e.g. 8" data-testid="hex-n2" /></div>
        <div><Label>{L(lang, "数字三", "Number 3")}</Label><Input value={n3} onChange={(e) => setN3(e.target.value)} inputMode="numeric" placeholder="e.g. 15" data-testid="hex-n3" /></div>
      </div>
      <div>
        <Label>{L(lang, "所问之事（选填）", "Your question (optional)")}</Label>
        <Textarea value={q} onChange={(e) => setQ(e.target.value)} rows={2} placeholder={L(lang, "例如：近期事业发展如何？", "e.g. How will my career develop?")} data-testid="hex-question" />
      </div>
      <Button onClick={run} disabled={busy} className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="hex-cast">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Compass className="h-4 w-4 mr-1" />}{L(lang, "起卦", "Cast")}
      </Button>

      {busy && <div className="py-8 text-center text-muted-foreground flex flex-col items-center gap-2"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-bronze))]" />{L(lang, "起卦与解卦中…", "Casting…")}</div>}

      {r && (
        <div data-testid="hex-result">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-muted-foreground">{L(lang, "起卦结果", "Result")}</span>
            <SaveButton lang={lang} onClick={() => saveRecord("hexagram", r, res.ai_reading, `${r.ben_gua.name} → ${r.bian_gua.name}`, lang)} />
          </div>
          <div className="flex items-center gap-3">
            <GuaCard title={L(lang, "本卦", "Primary")} gua={r.ben_gua} />
            <div className="text-center text-xs text-muted-foreground">
              <div className="text-[hsl(var(--brand-vermillion))] font-bold">{L(lang, `动爻 第${r.moving_line}爻`, `Line ${r.moving_line}`)}</div>
              <div className="text-2xl">→</div>
            </div>
            <GuaCard title={L(lang, "变卦", "Changing")} gua={r.bian_gua} />
          </div>
          {res.ai_reading?.guidance_one_liner && (
            <div className="mt-3 rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.25)] p-3 flex items-center gap-2" data-testid="hex-guidance">
              <Sparkles className="h-4 w-4 text-[hsl(var(--brand-bronze))] shrink-0" />
              <span className="text-sm font-semibold text-[hsl(var(--brand-ink))]">{res.ai_reading.guidance_one_liner}</span>
            </div>
          )}
          <Reading reading={res.ai_reading} lang={lang} />
          {!res.ai_reading && <p className="mt-3 text-xs text-muted-foreground">{L(lang, "（AI 解读暂时不可用，以上为起卦结果）", "(AI reading unavailable; cast result shown)")}</p>}
        </div>
      )}
    </div>
  );
}

const GRADE_STYLE = {
  "大吉": "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))]",
  "中吉": "bg-[hsl(var(--brand-gold)/0.7)] text-[hsl(var(--brand-ink))]",
  "小吉": "bg-[hsl(var(--brand-gold)/0.4)] text-[hsl(var(--brand-bronze))]",
  "平": "bg-muted text-muted-foreground",
  "凶": "bg-[hsl(var(--brand-vermillion)/0.15)] text-[hsl(var(--brand-vermillion))]",
};
const FIT_STYLE = {
  "很适合": "border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]",
  "适合": "border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]",
  "一般": "border-border text-muted-foreground",
  "不利": "border-[hsl(var(--brand-vermillion))] text-[hsl(var(--brand-vermillion))]",
};

const CARD_BLESSINGS = [
  { zh: "福寿安康", en: "Health & Longevity" },
  { zh: "吉祥如意", en: "Good Fortune" },
  { zh: "财源广进", en: "Abundant Wealth" },
  { zh: "花开富贵", en: "Blossoming Prosperity" },
  { zh: "平安喜乐", en: "Peace & Joy" },
];

function GreetingCardDialog({ number, lang, onClose }) {
  const canvasRef = React.useRef(null);
  const [blessing, setBlessing] = useState(CARD_BLESSINGS[0]);

  const draw = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 600, H = 800;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#2a1810");
    bg.addColorStop(1, "#3d2415");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "#C9A227";
    ctx.lineWidth = 6;
    ctx.strokeRect(24, 24, W - 48, H - 48);
    ctx.strokeStyle = "rgba(201,162,39,0.4)";
    ctx.lineWidth = 2;
    ctx.strokeRect(38, 38, W - 76, H - 76);
    ctx.textAlign = "center";
    ctx.fillStyle = "#C9A227";
    ctx.font = "600 26px 'Noto Sans SC', serif";
    ctx.fillText("你好功德 · CiAO Merit", W / 2, 96);
    ctx.fillStyle = "#F0D98C";
    ctx.font = "700 92px 'Noto Serif SC', serif";
    const bz = blessing.zh;
    if (bz.length === 4) {
      ctx.fillText(bz.slice(0, 2), W / 2, 260);
      ctx.fillText(bz.slice(2), W / 2, 360);
    } else {
      ctx.fillText(bz, W / 2, 300);
    }
    ctx.fillStyle = "rgba(240,217,140,0.7)";
    ctx.font = "400 22px 'Montserrat', sans-serif";
    ctx.fillText(blessing.en, W / 2, 410);
    ctx.fillStyle = "rgba(201,162,39,0.12)";
    ctx.fillRect(90, 470, W - 180, 150);
    ctx.strokeStyle = "#C9A227";
    ctx.lineWidth = 2;
    ctx.strokeRect(90, 470, W - 180, 150);
    ctx.fillStyle = "rgba(240,217,140,0.8)";
    ctx.font = "400 22px 'Noto Sans SC', serif";
    ctx.fillText(lang === "zh" ? "专属吉号" : "Your Lucky Number", W / 2, 512);
    ctx.fillStyle = "#F5E6B8";
    ctx.font = "700 62px 'Montserrat', monospace";
    ctx.fillText(number, W / 2, 586);
    ctx.fillStyle = "rgba(240,217,140,0.55)";
    ctx.font = "400 18px 'Noto Sans SC', serif";
    ctx.fillText(lang === "zh" ? "愿此号带来吉祥安康 · 诚意赠予" : "May this number bring blessings", W / 2, 700);
    ctx.font = "400 14px 'Noto Sans SC', serif";
    ctx.fillText(lang === "zh" ? "仅供参考，不构成任何决策建议" : "For reference only", W / 2, 740);
  }, [blessing, number, lang]);

  React.useEffect(() => { draw(); }, [draw]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `ciao_blessing_${number}.png`;
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link); link.click(); link.remove();
  };

  const share = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], `ciao_blessing_${number}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: L(lang, "吉号祝福贺卡", "Blessing Card") });
      } else {
        download();
        toast.success(L(lang, "已下载贺卡，可在相册中分享", "Card downloaded — share it from your gallery"));
      }
    } catch (e) { /* cancelled */ }
  };

  return (
    <Dialog open={!!number} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="greeting-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5"><Gift className="h-4 w-4 text-[hsl(var(--brand-bronze))]" />{L(lang, "祝福贺卡", "Blessing Card")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <canvas ref={canvasRef} width={600} height={800} className="w-full rounded-lg border border-border" data-testid="greeting-card-canvas" />
          <div>
            <div className="text-xs text-muted-foreground mb-1">{L(lang, "选择祝福语", "Choose a blessing")}</div>
            <div className="flex flex-wrap gap-1.5">
              {CARD_BLESSINGS.map((b) => (
                <Button key={b.zh} type="button" size="sm" variant={blessing.zh === b.zh ? "default" : "outline"}
                  className={`h-7 px-2.5 text-xs ${blessing.zh === b.zh ? "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-gold))]" : ""}`}
                  onClick={() => setBlessing(b)} data-testid={`card-blessing-${b.zh}`}>
                  {L(lang, b.zh, b.en)}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={download} className="flex-1 bg-[hsl(var(--primary))] text-primary-foreground" data-testid="card-download"><Download className="h-4 w-4 mr-1" />{L(lang, "下载图片", "Download")}</Button>
            <Button onClick={share} variant="outline" className="flex-1 border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" data-testid="card-share"><Share2 className="h-4 w-4 mr-1" />{L(lang, "分享", "Share")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CandidateCard({ c, idx, lang }) {
  const [open, setOpen] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const gradeZh = c.grade_zh || "";
  const gradeStyle = GRADE_STYLE[gradeZh] || "bg-muted text-muted-foreground";
  return (
    <div className={`rounded-xl border bg-[hsl(var(--brand-gold-soft)/0.12)] p-3 ${c.fav_match ? "border-[hsl(var(--brand-gold))] ring-1 ring-[hsl(var(--brand-gold)/0.5)]" : "border-[hsl(var(--brand-gold))]"}`} data-testid={`picker-cand-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="font-mono-num text-xl font-bold tracking-widest text-[hsl(var(--brand-ink))]">{c.full}</span>
          {c.fav_match && (
            <Badge className="bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))] text-[9px] px-1.5 py-0" title={L(lang, "契合你的命盘喜用五行", "Matches your chart's favourable elements")} data-testid={`picker-fav-${idx}`}>
              {L(lang, "喜用", "Fav")}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {gradeZh && <Badge className={gradeStyle} data-testid={`picker-grade-${idx}`}>{L(lang, gradeZh, c.grade_en || gradeZh)}</Badge>}
          <Badge variant="outline" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" title={L(lang, "综合评分", "Score")}>{c.score}</Badge>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-[hsl(var(--brand-bronze))]" title={L(lang, "生成祝福贺卡", "Blessing card")}
            onClick={() => setShowCard(true)} data-testid={`picker-card-${idx}`}>
            <Gift className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-[hsl(var(--brand-bronze))]" title={L(lang, "加入对比", "Add to compare")}
            onClick={() => saveRecord("lucky", c, null, c.full, lang, L(lang, "已加入对比", "Added to compare"))} data-testid={`picker-add-${idx}`}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {showCard && <GreetingCardDialog number={c.full} lang={lang} onClose={() => setShowCard(false)} />}
      <div className="mt-1.5 flex flex-wrap gap-1">
        {c.segments.map((s, j) => (
          <span key={j} className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold ${s.type === "auspicious" ? "bg-[hsl(var(--brand-gold)/0.25)] text-[hsl(var(--brand-bronze))]" : "bg-[hsl(var(--brand-vermillion)/0.12)] text-[hsl(var(--brand-vermillion))]"}`}>
            <span className="font-mono-num">{s.pair}</span>{s.star}
          </span>
        ))}
      </div>
      {(c.matched_intents || []).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {c.matched_intents.map((m) => <Badge key={m} variant="outline" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))] text-[10px]">{L(lang, m, (INTENTS.find((x) => x.key === m) || {}).en || m)}</Badge>)}
        </div>
      )}

      {(c.summary_zh || c.field_breakdown) && (
        <>
          <button
            onClick={() => setOpen((o) => !o)}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--brand-bronze))] hover:underline"
            data-testid={`picker-detail-toggle-${idx}`}
          >
            {open ? L(lang, "收起分析", "Hide analysis") : L(lang, "详细分析", "Detailed analysis")}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>

          {open && (
            <div className="mt-2 space-y-2.5 border-t border-[hsl(var(--brand-gold)/0.3)] pt-2.5" data-testid={`picker-detail-${idx}`}>
              {/* Summary */}
              <p className="text-xs leading-5 text-foreground/85">{L(lang, c.summary_zh, c.summary_en)}</p>

              {/* Dominant field report */}
              {c.dominant && (
                <div className="rounded-lg bg-[hsl(var(--brand-gold-soft)/0.25)] p-2">
                  <div className="text-[11px] font-semibold text-[hsl(var(--brand-ink))]">
                    {L(lang, "主导磁场", "Dominant field")}：{c.dominant} · {L(lang, c.dominant_label_zh, c.dominant_label_en)}
                  </div>
                  <p className="mt-0.5 text-[11px] leading-5 text-muted-foreground">{L(lang, c.dominant_report_zh, c.dominant_report_en)}</p>
                </div>
              )}

              {/* Field breakdown */}
              {(c.field_breakdown || []).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-[hsl(var(--brand-ink))] mb-1">{L(lang, "磁场构成", "Field composition")}</div>
                  <div className="space-y-1">
                    {c.field_breakdown.map((b, j) => (
                      <div key={j} className="flex items-start justify-between gap-2 text-[11px]">
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${b.type === "auspicious" ? "bg-[hsl(var(--brand-gold))]" : "bg-[hsl(var(--brand-vermillion))]"}`} />
                          <span className={b.type === "auspicious" ? "text-[hsl(var(--brand-bronze))] font-medium" : "text-[hsl(var(--brand-vermillion))] font-medium"}>{b.star}×{b.count}</span>
                        </div>
                        <span className="text-right text-muted-foreground leading-4">{L(lang, b.label_zh, b.label_en)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-intent fit */}
              {(c.per_intent || []).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-[hsl(var(--brand-ink))] mb-1">{L(lang, "心愿契合度", "Wish fit")}</div>
                  <div className="flex flex-wrap gap-1">
                    {c.per_intent.map((p) => (
                      <Badge key={p.intent} variant="outline" className={`text-[10px] ${FIT_STYLE[p.fit_zh] || "border-border text-muted-foreground"}`}>
                        {L(lang, p.intent, p.intent_en)} · {L(lang, p.fit_zh, p.fit_en)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LuckyPicker({ lang }) {
  const [seed, setSeed] = useState("");
  const [digits, setDigits] = useState(4);   // 2 | 3 | 4 | 'all'
  const [intents, setIntents] = useState([]);
  const [weights, setWeights] = useState({});  // { intent: 1|2|3 }
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);     // { candidates } or { groups }
  const [autoFilled, setAutoFilled] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [tplBusy, setTplBusy] = useState("");
  const [templates, setTemplates] = useState([]);
  const [mode, setMode] = useState("position");

  React.useEffect(() => {
    api.get("/numerology/lucky-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  const runTemplate = async (tpl) => {
    setTplBusy(tpl); setData(null);
    try {
      const d = digits === "all" ? 4 : digits;
      const r = await api.post("/numerology/lucky-template", { input: seed, digits: d, template: tpl, lang, mode });
      setData({ ...r.data, _template: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setTplBusy(""); }
  };

  const printAnalysis = () => {
    const list = data?.groups ? data.groups.flatMap((g) => g.candidates) : (data?.candidates || []);
    if (!list.length) { toast.error(L(lang, "暂无可导出的吉号", "Nothing to export")); return; }
    const esc = (v) => String(v ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const card = (c) => {
      const segs = (c.segments || []).map((s) => `<span class="seg ${s.type}">${esc(s.pair)}${esc(s.star)}</span>`).join("");
      const fb = (c.field_breakdown || []).map((b) => `<li><b>${esc(b.star)}×${b.count}</b> — ${esc(L(lang, b.label_zh, b.label_en))}</li>`).join("");
      const fit = (c.per_intent || []).map((p) => `<span class="fit">${esc(L(lang, p.intent, p.intent_en))}·${esc(L(lang, p.fit_zh, p.fit_en))}</span>`).join("");
      return `<div class="card">
        <div class="hd"><span class="num">${esc(c.full)}</span><span class="grade">${esc(L(lang, c.grade_zh, c.grade_en))} · ${c.score}</span></div>
        <div class="segs">${segs}</div>
        <p class="sum">${esc(L(lang, c.summary_zh, c.summary_en))}</p>
        ${fb ? `<div class="sub">${L(lang, "磁场构成", "Field composition")}</div><ul>${fb}</ul>` : ""}
        ${fit ? `<div class="sub">${L(lang, "心愿契合", "Wish fit")}</div><div class="fits">${fit}</div>` : ""}
      </div>`;
    };
    const win = window.open("", "_blank");
    if (!win) { toast.error(L(lang, "请允许弹出窗口", "Allow pop-ups")); return; }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${L(lang, "吉号分析", "Lucky Number Analysis")}</title>
      <style>body{font-family:'Noto Sans SC',Arial,sans-serif;color:#2b2b2b;padding:24px}
      h1{font-size:19px;margin:0 0 2px} .meta{color:#999;font-size:12px;margin-bottom:14px}
      .card{border:1px solid #e3d9c2;border-radius:10px;padding:12px 14px;margin-bottom:12px;page-break-inside:avoid}
      .hd{display:flex;justify-content:space-between;align-items:center} .num{font-size:20px;font-weight:700;letter-spacing:2px}
      .grade{font-size:12px;background:#faf1d6;border:1px solid #d9c38a;border-radius:20px;padding:2px 10px;color:#8a6d2f}
      .segs{margin:6px 0} .seg{display:inline-block;font-size:11px;padding:1px 6px;border-radius:4px;margin:2px}
      .seg.auspicious{background:#f6eccb;color:#8a6d2f} .seg.inauspicious{background:#f6dcd6;color:#a33b2b}
      .sum{font-size:12px;line-height:1.6;margin:6px 0;color:#444}
      .sub{font-size:11px;font-weight:700;margin-top:6px;color:#8a6d2f} ul{margin:4px 0;padding-left:18px;font-size:11px;line-height:1.6}
      .fits .fit{display:inline-block;font-size:11px;border:1px solid #d9c38a;border-radius:20px;padding:1px 8px;margin:2px;color:#8a6d2f}
      </style></head><body>
      <h1>${L(lang, "你好功德 · 吉号详细分析", "CiAO Merit · Lucky Number Analysis")}</h1>
      <div class="meta">${data?.label_zh ? esc(L(lang, data.label_zh, data.label_en)) + " · " : ""}${L(lang, "生成时间", "Generated")}: ${new Date().toLocaleString(lang === "zh" ? "zh-CN" : "en-US")}</div>
      ${list.map(card).join("")}
      <p class="meta">${L(lang, "仅供参考，不构成任何决策建议", "For reference only; not decision advice")}</p>
      </body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 350);
  };

  // 命盘联动: 依据喜用五行自动带入默认心愿(仅当用户尚未选择时)
  React.useEffect(() => {
    api.get("/numerology/lucky-numbers", { params: { lang } })
      .then((r) => {
        const sug = r.data.suggested_intents || [];
        if (!sug.length) return;
        setIntents((prev) => {
          if (prev.length) return prev;
          setAutoFilled(true);
          return sug;
        });
      })
      .catch(() => {});
  }, [lang]);

  // 选中的心愿默认权重 2(一般); 取消选中则移除权重
  React.useEffect(() => {
    setWeights((prev) => {
      const next = {};
      intents.forEach((it) => { next[it] = prev[it] || 2; });
      return next;
    });
  }, [intents]);

  const WEIGHT_OPTS = [{ v: 1, zh: "次要", en: "Minor" }, { v: 2, zh: "一般", en: "Normal" }, { v: 3, zh: "主要", en: "Major" }];

  const run = async () => {
    setBusy(true); setData(null);
    try {
      const payload = { input: seed, intents, intent_weights: weights, lang, mode };
      if (digits === "all") {
        const r = await api.post("/numerology/lucky-picker/all", payload);
        setData(r.data);
      } else {
        const r = await api.post("/numerology/lucky-picker", { ...payload, digits });
        setData(r.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const saveMany = async (list, msgCount) => {
    if (!list?.length) return;
    setSavingAll(true);
    try {
      await Promise.all(list.map((c) =>
        api.post("/numerology/save", { kind: "lucky", result: c, ai_reading: null, label: c.full })));
      toast.success(L(lang, `已把 ${msgCount} 个吉号加入对比`, `Added ${msgCount} numbers to compare`));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSavingAll(false); }
  };

  const digitOptions = [2, 3, 4, "all"];
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>{L(lang, "输入数字或英文字母（选填）", "Digits or letters (optional)")}</Label>
          <Input value={seed} onChange={(e) => setSeed(e.target.value)} placeholder={L(lang, "如 8 或 AB（字母按所选模式换算）", "e.g. 8 or AB (letters converted)")} data-testid="picker-seed" />
          <div className="mt-2"><ModeToggle mode={mode} setMode={setMode} lang={lang} /></div>
        </div>
        <div>
          <Label>{L(lang, "推荐位数", "Digits")}</Label>
          <div className="flex gap-2 mt-1" data-testid="picker-digits">
            {digitOptions.map((d) => (
              <Button key={d} type="button" size="sm" variant={digits === d ? "default" : "outline"}
                className={digits === d ? "bg-[hsl(var(--primary))] text-primary-foreground" : ""}
                onClick={() => setDigits(d)} data-testid={`picker-digit-${d}`}>
                {d === "all" ? L(lang, "全部位数", "All") : `${d}${L(lang, "位", "")}`}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <Label>{L(lang, "所求心愿（可多选，不选按总分排序）", "Wishes (multi-select; none = by total score)")}</Label>
        {autoFilled && intents.length > 0 && (
          <p className="text-[11px] text-[hsl(var(--brand-bronze))] mt-0.5" data-testid="picker-autofill-hint">{L(lang, "已根据你的命盘喜用五行自动带入默认心愿，可自行调整。", "Default wishes auto-filled from your chart's favourable elements — adjust freely.")}</p>
        )}
        <ToggleGroup type="multiple" value={intents} onValueChange={setIntents} className="justify-start flex-wrap mt-1" data-testid="picker-intents">
          {INTENTS.map((it) => (
            <ToggleGroupItem key={it.key} value={it.key} data-testid={`picker-intent-${it.key}`}
              className="border border-border data-[state=on]:bg-[hsl(var(--brand-gold))] data-[state=on]:text-[hsl(var(--brand-ink))] data-[state=on]:border-[hsl(var(--brand-gold))]">
              {L(lang, it.key, it.en)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {intents.length > 1 && (
          <div className="mt-3 space-y-1.5 rounded-lg border border-border p-3" data-testid="picker-weights">
            <div className="text-[11px] text-muted-foreground">{L(lang, "为多个心愿设主次权重，排序更贴合你最看重的目标：", "Set primary/secondary weights so ranking matches your top goal:")}</div>
            {intents.map((it) => (
              <div key={it} className="flex items-center gap-2" data-testid={`picker-weight-row-${it}`}>
                <span className="w-14 text-sm font-medium text-[hsl(var(--brand-ink))]">{L(lang, it, (INTENTS.find((x) => x.key === it) || {}).en || it)}</span>
                <div className="flex gap-1">
                  {WEIGHT_OPTS.map((o) => (
                    <Button key={o.v} type="button" size="sm" variant={weights[it] === o.v ? "default" : "outline"}
                      className={`h-7 px-2.5 text-xs ${weights[it] === o.v ? "bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-gold))]" : ""}`}
                      onClick={() => setWeights((w) => ({ ...w, [it]: o.v }))} data-testid={`picker-weight-${it}-${o.v}`}>
                      {L(lang, o.zh, o.en)}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Button onClick={run} disabled={busy} className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="picker-run">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Star className="h-4 w-4 mr-1" />}{L(lang, "生成吉号", "Generate")}
      </Button>

      {/* 健康吉号模板: 天医磁场一键优选 */}
      <div className="rounded-lg border border-[hsl(var(--brand-gold)/0.5)] bg-[hsl(var(--brand-gold-soft)/0.14)] p-3" data-testid="picker-templates">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--brand-bronze))]">
          <HeartPulse className="h-4 w-4" />{L(lang, "健康吉号模板", "Health Templates")}
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">{L(lang, "以「天医磁场」为核心一键优选，自动结合你的命盘喜用数。", "One-tap picks built around the Tian Yi (health) field, blended with your chart's favourable digits.")}</p>
        <div className="flex flex-wrap gap-2">
          {templates.map((t) => (
            <Button key={t.key} type="button" size="sm" variant="outline" disabled={!!tplBusy} onClick={() => runTemplate(t.key)}
              title={L(lang, t.desc_zh, t.desc_en)}
              className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" data-testid={`picker-template-${t.key}`}>
              {tplBusy === t.key ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <HeartPulse className="h-4 w-4 mr-1" />}{L(lang, t.label_zh, t.label_en)}
            </Button>
          ))}
        </div>
      </div>

      {(busy || tplBusy) && <div className="py-8 text-center text-muted-foreground flex flex-col items-center gap-2"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-bronze))]" />{L(lang, "推算中…", "Computing…")}</div>}

      {/* 喜用神联动说明 + 模板横幅 */}
      {data && (data._template || data.has_chart !== undefined) && (
        <div className="space-y-2">
          {data._template && (
            <div className="rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.2)] p-3" data-testid="picker-template-banner">
              <div className="text-sm font-semibold text-[hsl(var(--brand-bronze))] flex items-center gap-1.5"><HeartPulse className="h-4 w-4" />{L(lang, data.label_zh, data.label_en)}</div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{L(lang, data.desc_zh, data.desc_en)}</p>
            </div>
          )}
          {data.has_chart && (data.favored_digits || []).length > 0 && (
            <div className="rounded-lg border border-[hsl(var(--brand-gold)/0.4)] bg-[hsl(var(--brand-gold-soft)/0.12)] px-3 py-2 text-[11px]" data-testid="picker-favored-legend">
              <span className="text-[hsl(var(--brand-bronze))] font-medium">{L(lang, "命盘喜用五行", "Favourable elements")}：</span>
              <span className="text-foreground">{(data.favored_elements || []).join(" / ") || "—"}</span>
              <span className="mx-1.5 text-muted-foreground">·</span>
              <span className="text-[hsl(var(--brand-bronze))] font-medium">{L(lang, "喜用数", "Lucky digits")}：</span>
              <span className="font-mono-num text-foreground">{(data.favored_digits || []).join(" ")}</span>
              <span className="ml-1.5 text-muted-foreground">{L(lang, "（含此类数字的吉号已标「喜用」并优先）", "(numbers with these are tagged 'Favourable' and ranked higher)")}</span>
            </div>
          )}
          {data.has_chart === false && (
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground" data-testid="picker-nochart-hint">
              {L(lang, "完善「个人页面」出生年月日与时辰后，吉号会结合你的命盘喜用五行自动高亮契合号码。", "Complete your birth date & time in Profile to auto-highlight numbers matching your chart's favourable elements.")}
            </div>
          )}
        </div>
      )}

      {data && data.groups && (
        <div data-testid="picker-result-all" className="space-y-5">
          {data.normalized_input && (
            <p className="text-xs text-muted-foreground">{L(lang, "输入换算为数字：", "Input mapped to digits: ")}<span className="font-mono-num text-[hsl(var(--brand-bronze))]">{data.normalized_input}</span></p>
          )}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={printAnalysis} className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" data-testid="picker-print-all">
              <Printer className="h-4 w-4 mr-1" />{L(lang, "打印分析", "Print")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => saveMany(data.groups.flatMap((g) => g.candidates), data.groups.reduce((n, g) => n + g.candidates.length, 0))} disabled={savingAll} className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" data-testid="picker-add-all">
              {savingAll ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Scale className="h-4 w-4 mr-1" />}{L(lang, "全部加入对比", "Add all to compare")}
            </Button>
          </div>
          {data.groups.map((g) => (
            <div key={g.digits} data-testid={`picker-group-${g.digits}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--brand-bronze))]"><Star className="h-4 w-4" />{g.digits}{L(lang, " 位最旺推荐", "-digit picks")}</span>
                <Button size="sm" variant="ghost" onClick={() => saveMany(g.candidates, g.candidates.length)} disabled={savingAll} className="h-7 text-[hsl(var(--brand-bronze))]" data-testid={`picker-group-addall-${g.digits}`}>
                  <Plus className="h-3.5 w-3.5 mr-1" />{L(lang, "整组加入", "Add group")}
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {g.candidates.map((c, i) => <CandidateCard key={i} c={c} idx={`${g.digits}-${i}`} lang={lang} />)}
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">{L(lang, "已按 2/3/4 位分组展示各自最旺推荐；点「＋」或「整组加入」收藏到对比。", "Top picks grouped by 2/3/4 digits; tap + or 'Add group' to collect for comparison.")}</p>
        </div>
      )}

      {data && data.candidates && (
        <div data-testid="picker-result" className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {data.normalized_input ? (
              <p className="text-xs text-muted-foreground">{L(lang, "输入换算为数字：", "Input mapped to digits: ")}<span className="font-mono-num text-[hsl(var(--brand-bronze))]">{data.normalized_input}</span></p>
            ) : <span className="text-xs text-muted-foreground">{L(lang, `共 ${data.candidates.length} 个候选吉号`, `${data.candidates.length} candidates`)}</span>}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={printAnalysis} className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" data-testid="picker-print">
                <Printer className="h-4 w-4 mr-1" />{L(lang, "打印分析", "Print")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => saveMany(data.candidates, data.candidates.length)} disabled={savingAll} className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]" data-testid="picker-add-all">
                {savingAll ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Scale className="h-4 w-4 mr-1" />}{L(lang, "全部加入对比", "Add all to compare")}
              </Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.candidates.map((c, i) => <CandidateCard key={i} c={c} idx={i} lang={lang} />)}
          </div>
          <p className="text-xs text-muted-foreground">{L(lang, "综合八星磁场与所求心愿排序；点「＋」可加入对比。", "Ranked by Eight-Star fields and your wishes; tap + to add to compare.")}</p>
        </div>
      )}
    </div>
  );
}

function ChartLucky({ lang }) {
  const [busy, setBusy] = useState(true);
  const [data, setData] = useState(null);
  const [incomplete, setIncomplete] = useState(false);

  React.useEffect(() => {
    setBusy(true);
    api.get("/numerology/lucky-numbers", { params: { lang } })
      .then((r) => { setData(r.data); setIncomplete(false); })
      .catch((e) => { if (e.response?.status === 400) setIncomplete(true); else setData(null); })
      .finally(() => setBusy(false));
  }, [lang]);

  if (busy) return <div className="py-6 text-center text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (incomplete) return (
    <div className="rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.2)] p-4 text-sm" data-testid="lucky-incomplete">
      {L(lang, "完善「个人页面」出生年月日与时辰后，这里会显示你命盘的喜用五行与吉数。", "Complete your birth date & time in Profile to see your chart's favourable elements & digits here.")}
    </div>
  );
  if (!data) return null;

  return (
    <div className="rounded-xl border border-border p-4" data-testid="lucky-chart-panel">
      <div className="text-xs text-muted-foreground mb-1">{L(lang, "命盘喜用五行 · 吉数（河图数）", "Favourable elements & digits (He Tu)")}</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {(data.needed_elements || []).map((e) => (
          <span key={e} className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft)/0.2)] px-2.5 py-1 text-sm font-semibold text-[hsl(var(--brand-bronze))]">
            {e} · {(data.element_digits?.[e] || []).join("/")}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {(data.lucky_digits || []).map((d) => (
          <span key={d} className="grid h-8 w-8 place-items-center rounded-full bg-[hsl(var(--brand-gold))] text-[hsl(var(--brand-ink))] font-mono-num font-bold text-sm">{d}</span>
        ))}
      </div>
      {(data.avoid_digits || []).length > 0 && (
        <div className="mt-2 text-xs text-muted-foreground">{L(lang, "宜少用", "Use sparingly")}：<span className="font-mono-num">{data.avoid_digits.join(" ")}</span></div>
      )}
    </div>
  );
}

function LuckyTool({ lang }) {
  return (
    <div className="space-y-5">
      <ChartLucky lang={lang} />
      <div>
        <div className="flex items-center gap-2 mb-2 text-[hsl(var(--brand-bronze))]"><Star className="h-4 w-4" /><span className="text-sm font-semibold">{L(lang, "吉号选择器", "Lucky number picker")}</span></div>
        <LuckyPicker lang={lang} />
      </div>
    </div>
  );
}

export default function Numerology() {
  const { lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "phone";
  const setTab = (v) => setParams({ tab: v }, { replace: true });

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="numerology-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[hsl(var(--brand-ink))]">{L(lang, "易经数字", "Yijing Numbers")}</h1>
        <p className="text-muted-foreground mt-1">{L(lang, "数字能量（手机/身份证/车牌等，可含英文字母）· 易经数字起卦 · 吉号选择 · 号码对比，结合规则计算与 AI 解读。", "Number energy (phone/IC/plate, letters ok), Yijing divination, lucky picker & compare — rule-based analysis with AI reading.")}</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="phone" data-testid="tab-phone"><Smartphone className="h-4 w-4 mr-1.5" />{L(lang, "数字能量", "Energy")}</TabsTrigger>
          <TabsTrigger value="hexagram" data-testid="tab-hexagram"><Hexagon className="h-4 w-4 mr-1.5" />{L(lang, "易经起卦", "Hexagram")}</TabsTrigger>
          <TabsTrigger value="lucky" data-testid="tab-lucky"><Star className="h-4 w-4 mr-1.5" />{L(lang, "吉利号码", "Lucky No.")}</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare"><Scale className="h-4 w-4 mr-1.5" />{L(lang, "对比", "Compare")}</TabsTrigger>
        </TabsList>
        <TabsContent value="phone" className="mt-5"><PhoneTool lang={lang} /></TabsContent>
        <TabsContent value="hexagram" className="mt-5"><HexagramTool lang={lang} /></TabsContent>
        <TabsContent value="lucky" className="mt-5"><LuckyTool lang={lang} /></TabsContent>
        <TabsContent value="compare" className="mt-5"><NumberCompare /></TabsContent>
      </Tabs>
      <p className="mt-8 text-xs text-muted-foreground flex items-center gap-1.5">
        <TriangleAlert className="h-3.5 w-3.5" />{L(lang, "仅供参考，不构成任何决策建议。", "For reference only; not decision advice.")}
      </p>
    </div>
  );
}
